import _ from 'underscore';

/**
 * TaskDataService class handles task data operations such as reading, writing, updating, and deleting task entries.
 * It also manages caching and consistency of task data.
 */
class TaskDataService {
    /**
     * Constructor initializes the TaskDataService with necessary dependencies and sets up internal state.
     * @param {Object} $http - AngularJS HTTP service.
     * @param {Object} $bridges - Service for bridging data.
     * @param {Object} $objectCache - Cache service for objects.
     * @param {Class} RepositoryEntry - Class representing a repository entry.
     * @param {Object} TaskInfoService - Service for task information.
     * @param {Object} $config - Configuration service.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $q - AngularJS promise service.
     * @param {Object} $signals - Signal service.
     * @param {Object} APIService - API service for backend communication.
     * @param {Object} $rootScope - AngularJS root scope.
     */
    constructor($http, $bridges, $objectCache, RepositoryEntry, TaskInfoService, $config, $injector, $q, $signals, APIService, $rootScope) {
        this._logIdentifier = 'TaskDataService';
        this.DEVMODE = false;
        this.$logger = $bridges.logger();
        this._contextCache = null;
        this._repository = null;
        this._repositoryClone = null;
        this._entryCache = {};
        this._entriesDirty = [];
        this._entriesDeleted = [];
        this.VersioningAndLockingService = null;
        this._ConsistencyDataService = null;
        this.$http = $http;
        this.$bridges = $bridges;
        this.$objectCache = $objectCache;
        this.RepositoryEntry = RepositoryEntry;
        this.TaskInfoService = TaskInfoService;
        this.$config = $config;
        this.$injector = $injector;
        this.$q = $q;
        this.$signals = $signals;
        this.APIService = APIService;
        this.$rootScope = $rootScope;
    }

    /**
     * Retrieves the ConsistencyDataService instance.
     * @returns {Object} ConsistencyDataService instance.
     */
    getConsistencyDataService() {
        if (!this._ConsistencyDataService) {
            this._ConsistencyDataService = this.$injector.get('ConsistencyDataService');
        }
        return this._ConsistencyDataService;
    }

    /**
     * Commits the current state of the data store.
     * @returns {Promise} A promise that resolves when the commit is successful, or rejects with an error.
     */
    commit() {
        console.log("Start Commit");
        const promise = this.$q.defer();

        if (angular.isFunction(this.$bridges.data().commitStore)) {
            this.$bridges.data().commitStore((success_, data) => {
                if (success_) {
                    promise.resolve();
                } else {
                    promise.reject(data.error);
                }
                this._repositoryClone = angular.copy(this._repository);
            });
        } else {
            promise.reject("'commitStore' Function not exists");
        }

        return promise.promise;
    }

    /**
     * Clears all preview data from the cache.
     */
    clearAllPreviewData() {
        this.$injector.get("DataBridgeService").clear();
        Object.keys(this._entryCache).forEach(euuid => {
            const entryObj = this._entryCache[euuid];

            if (typeof(entryObj.previewDiff) !== "undefined") {
                console.log("clearAllPreviewData2", entryObj, entryObj.getUUID());
                entryObj.previewDiff = null;
                delete entryObj.previewDiff;
                console.log("clearAllPreviewData2b", entryObj, entryObj.getUUID());
            }

            const cacheObj = this.$objectCache.get(euuid);
            if (typeof(cacheObj.previewDiff) !== "undefined") {
                console.log("clearAllPreviewData3", cacheObj, cacheObj.getUUID());
                cacheObj.previewDiff = null;
                delete cacheObj.previewDiff;
                console.log("clearAllPreviewData3b", cacheObj, cacheObj.getUUID());
            }
        });
    }

    /**
     * Saves all changes made to the entries.
     * @returns {Promise} A promise that resolves when all changes are saved, or rejects with an error.
     */
    saveAllChanges() {
        console.info("Saving all changes");

        const future = this.$q.defer();

        const savePromises = [];
        for (const entry in this._entriesDirty) {
            if (this._entriesDirty.hasOwnProperty(entry)) {
                const entryObj = this.read(entry);

                console.log("saving: ", entryObj);
                const savePromise = entryObj.save();
                savePromises.push(savePromise);
            }
        }

        if (savePromises.length === 0) {
            future.resolve();
            return future.promise;
        }

        let numPromisesToKeep = savePromises.length;

        const onPromiseSuccess = entity => {
            numPromisesToKeep--;
            console.log("Fulfilled promise for", entity, " - ", numPromisesToKeep, "promises left to fulfill");
            if (entity) {
                delete this._entriesDirty[entity.getUUID()];
            }
        };

        const onPromiseFulfilled = () => {
            console.log('onPromiseFulfilled', numPromisesToKeep);
            if (numPromisesToKeep === 0) {
                numPromisesToKeep--;

                this.commit().then(() => {
                    console.log("all changes saved!");
                    future.resolve();
                    this._entriesDirty = [];
                }, err => {
                    console.log('Commit Error' + err);
                    future.reject('Commit Error' + err);
                    this._entriesDirty = [];
                });
            }
        };

        const onPromiseRejected = (entity, err) => {
            console.error("Promise rejected", entity, err);
        };

        angular.forEach(savePromises, savePromise => {
            savePromise.then(onPromiseSuccess, onPromiseRejected).finally(onPromiseFulfilled);
        });

        return future.promise;
    }

    /**
     * Checks if there are any dirty entries.
     * @returns {boolean} True if there are dirty entries, false otherwise.
     */
    hasDirtyEntries() {
        for (const entryDirty in this._entriesDirty) {
            if (this._entriesDirty.hasOwnProperty(entryDirty)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Requires task data and optionally calls a callback with the task data and error status.
     * @param {Function} callback - Callback function to be called with task data and error status.
     * @returns {Object} The task data repository.
     */
    requireTaskData(callback) {
        const METHOD = 'requireTaskData';
        const hasCallback = typeof callback === 'function';

        this.$logger.logEnter(this._logIdentifier, METHOD);

        const onRequireCompleted = error => {
            if (hasCallback) {
                callback(this._repository, error);
            }
        };

        if (!this._repository) {
            const taskInfo = this.TaskInfoService.getTaskInfo();
            const files = taskInfo.getTaskDataFiles();

            if (this.isCMS || this.$rootScope.isTestingMode) {
                const uuid = taskInfo.cms_getUUID();

                this.APIService.getEntityList(uuid, true)
                    .then(response => {
                        if (angular.isFunction(this.$bridges.data().clear)) {
                            console.log('%cClearing data hashmap!', 'color:red;');
                            this.$bridges.data().clear();
                        }
                        const data = response.data.objects;
                        this._repository = {};
                        this._repositoryClone = {};
                        this._entryCache = {};
                        this._entriesDirty = {};

                        angular.forEach(data, (value, key) => {
                            this._repository[key] = value;
                            if (!angular.isDefined(this._entryCache[key])) {
                                const ModelFactory = this.getModelFactory(value.type);
                                if (ModelFactory) {
                                    this._entryCache[key] = this.$injector.instantiate(ModelFactory, { entry: value, TaskDataService: this });
                                } else {
                                    // Fallback to RepositoryEntry if no specific Model factory exists
                                    this._entryCache[key] = this.$injector.instantiate(this.RepositoryEntry, { entry: value, TaskDataService: this });
                                }
                                this._entryCache[key].setAsSaved();

                                if (this.isCMS) {
                                    this.getConsistencyDataService().isConsistency(this._entryCache[key], data);
                                }
                            }
                        });

                        if (this.isCMS) {
                            angular.forEach(data, (value, key) => {
                                this.read(key);
                            });
                        }

                        this.APIService.getResponseData().then(data => {
                            console.log("RESPAPPLY: Received response data from API, now applying");
                            if (data) {
                                angular.forEach(data, (value, index) => {
                                    console.log("RESPAPPLY: Applying ", uuid, " data", value);
                                    if (angular.isUUID(index)) {
                                        const uuid = index;
                                        if (this.exists(uuid)) {
                                            console.log("RESPAPPLY: Parsing value");
                                            const data = JSON.parse(JSON.parse(value));
                                            console.log("RESPAPPLY: Parsed value", data);
                                            console.log("RESPAPPLY: reading object");
                                            const obj = this.read(uuid);
                                            console.log("RESPAPPLY: Setting attachement");
                                            obj.setAttachement(data.attachement);
                                            console.log("RESPAPPLY: Saving", obj, JSON.stringify(obj.data), JSON.stringify(obj.getAttachement()));
                                            obj.save();
                                            console.log("RESPAPPLY: Saved", obj, JSON.stringify(obj.data), JSON.stringify(obj.getAttachement()));
                                        } else {
                                            console.log("RESPAPPLY: Not found", uuid);
                                        }
                                    }
                                });
                            }
                        });

                        this._repositoryClone = angular.copy(this._repository);

                        this.amendUUIDs();
                        onRequireCompleted.call(this, false);

                    }, error => {
                        console.error(error);
                        onRequireCompleted.call(this, true);
                    });

            } else {
                for (let fi = 0; fi < files.length; fi++) {
                    files[fi] = this.$config.getTaskContentFolderPath() + files[fi];
                }

                this._repository = {};
                this._repositoryClone = {};

                this._entryCache = {};
                this._entriesDirty = {};

                let unresolvedFilesAmount = files.length;

                const onFileLoaded = () => {
                    unresolvedFilesAmount--;
                    if (unresolvedFilesAmount < 1) {
                        this.amendUUIDs();
                        onRequireCompleted.call(this, false);
                    }
                };

                for (let idx = 0; idx < files.length; idx++) {
                    this.$logger.info(this._logIdentifier, "LOADING TASK DATA FILE: ", files[idx]);
                    this.$http.get(files[idx]).then(response => {
                        for (const key in response.data.repository) {
                            if (response.data.repository.hasOwnProperty(key)) {
                                this._repository[key] = response.data.repository[key];
                            }
                        }
                        this._repositoryClone = angular.copy(this._repository);
                        onFileLoaded();
                    });
                }
            }
        } else {
            onRequireCompleted.call(this, false);
        }

        this.$logger.logExit(this._logIdentifier, METHOD);

        return this._repository;
    }

    /**
     * Amends UUIDs in the repository to ensure they are correctly set.
     */
    amendUUIDs() {
        for (const uuid in this._repository) {
            if (this._repository.hasOwnProperty(uuid) && !angular.isDefined(this._repository[uuid].uuid)) {
                this._repository[uuid].uuid = uuid;
                console.info('undefined UUID attribute for object ' + this._repository[uuid] + '! Now amending UUID', uuid);
            }
        }
        this._repositoryClone = angular.copy(this._repository);
    }

    /**
     * Reads an entry by UUID.
     * @param {string} entryUUID - The UUID of the entry to read.
     * @param {boolean} [errorIfNotFound=true] - Whether to throw an error if the entry is not found.
     * @returns {Object|null} The entry object or null if not found.
     */
    readEntryByUUID(entryUUID, errorIfNotFound = true) {
        const METHOD = 'readEntryByUUID';
        this.$logger.logEnter(this._logIdentifier, METHOD, entryUUID);

        if (this.isCMS && !this.$config.inAssessment()) {
            return null;
        }

        let result = this._repository ? this._repository[entryUUID] : undefined;
        let message;

        if (result === undefined) {
            if (!errorIfNotFound) {
                this.$logger.warn(this, "Entry " + entryUUID + " not found");
                return null;
            }
            message = 'could not fetch entry. ';
            message += 'task data was not loaded. ';
            message += 'object uuid: ' + entryUUID;
        }

        if (typeof message === 'string') {
            this.$logger.error(this, message);
        }

        this.$logger.logExit(this._logIdentifier, METHOD);

        if (result) {
            if (!this._entryCache[result.uuid]) {
                const ModelFactory = this.getModelFactory(result.type);
                if (ModelFactory) {
                    this._entryCache[result.uuid] = this.$injector.instantiate(ModelFactory, { entry: result });
                } else {
                    // Fallback to RepositoryEntry if no specific Model factory exists
                    this._entryCache[result.uuid] = this.$injector.instantiate(this.RepositoryEntry, { entry: result });
                }
            }
            result = this._entryCache[result.uuid];
        }

        return result;
    }

    /**
     * Helper method to get the appropriate Model factory based on the type.
     * @param {string} type - The type of the entry.
     * @returns {Function|null} The Model factory or null if not found.
     */
    getModelFactory(type) {
        switch (type) {
            case 'task':
                return this.$injector.get('Task'); // Use $injector to get the factory function for Task
            case 'sheet':
                return this.$injector.get('Sheet'); // Use $injector to get the factory function for Sheet
            case 'questionPart':
                return this.$injector.get('QuestionPart'); // Use $injector to get the factory function for QuestionPart
            case 'container':
                return this.$injector.get('Container'); // Use $injector to get the factory function for Container
            case 'layout':
                return this.$injector.get('Layout'); // Use $injector to get the factory function for Layout
            // ... add other cases for different models
            default:
                return null;
        }
    }

    /**
     * Checks if an entry with the given UUID exists in the repository.
     * @param {string} uuid - The UUID to check.
     * @returns {boolean} True if the entry exists, false otherwise.
     */
    exists(uuid) {
        return this._repository && this._repository.hasOwnProperty(uuid);
    }

    /**
     * Fetches an entry having a specific attribute an
d type.
     * @param {string} attributeName - The attribute name to search for.
     * @param {string} requiredType - The required type of the entry.
     * @returns {Object|null} The entry object or null if not found.
     */
    fetchEntryHaving(attributeName, requiredType) {
        const METHOD = 'fetchEntryHaving';
        this.$logger.logEnter(this, METHOD, attributeName, requiredType);

        let result;
        let message;
        let validType = true;

        if (this._repository === undefined) {
            message = 'could not fetch entry. ';
            message += 'task data empty.';
        } else {
            for (const key in this._repository) {
                const entry = this._repository[key];

                if (entry.hasOwnProperty(attributeName)) {
                    if (requiredType) {
                        validType = typeof entry === requiredType;
                    }

                    if (validType) {
                        result = entry;
                        break;
                    }
                }
            }
        }

        if (typeof message === 'string') {
            this.$logger.error(this, message);
        }

        this.$logger.logExit(this, METHOD);

        if (result) {
            result = new this.RepositoryEntry(
                this.fetchContext(),
                result
            );
        }

        return result;
    }

    /**
     * Reads an instance of a specific class by UUID.
     * @param {string} uuid - The UUID of the instance.
     * @param {Class} Clazz - The class of the instance.
     * @returns {Object|null} The instance object or null if not found.
     */
    readInstanceOf(uuid, Clazz) {
        let result = this.$objectCache.get(uuid);

        if (!result) {
            result = this.readEntryByUUID(uuid);

            if (result.type !== undefined) {
                return this.readInstanceOfKnownType(uuid);
            } else {
                console.info("type is undefined");
            }

            if (result) {
                result = new Clazz(result);
                this.$objectCache.put(result);
            }
        }

        return result;
    }

    /**
     * Reads an instance of a known type by UUID.
     * @param {string} uuid - The UUID of the object to read.
     * @param {boolean} [errorIfNotFound=false] - Whether to throw an error if the object is not found.
     * @returns {Object|null} The instance object or null if not found.
     */
    readInstanceOfKnownType(uuid, errorIfNotFound) {
        let result = this.$objectCache.get(uuid);

        if (!result) {
            const entry = this.readEntryByUUID(uuid, errorIfNotFound);
            if (entry) {
                // Directly use the cached entry
                result = entry;
                this.$objectCache.put(result);
            }
        }

        return result;
    }

    /**
     * Reads an entry by UUID.
     * @param {string} uuid - The UUID of the entry to read.
     * @param {boolean} [errorIfNotFound=false] - Whether to throw an error if the entry is not found.
     * @returns {Object|null} The entry object or null if not found.
     */
    read(uuid, errorIfNotFound) {
        return this.readInstanceOfKnownType(uuid, errorIfNotFound);
    }

    /**
     * Writes an object to the repository with a given UUID.
     * @param {string} uuid - The UUID to assign to the object. If not provided, a new UUID will be generated.
     * @param {Object} obj - The object to write.
     * @returns {Object} The written object.
     */
    write(uuid, obj) {
        if (!angular.isDefined(uuid) || uuid == null || uuid === "") {
            uuid = angular.createUUID();
        }
        this._repository[uuid] = obj;
        if (obj.type !== 'media') {
            this.flagEntityAsDirty(uuid);
        }
        this.amendUUIDs();

        return this.readEntryByUUID(uuid);
    }

    /**
     * Updates an existing object in the repository.
     * @param {string} uuid - The UUID of the object to update.
     * @param {Object} obj - The new object data to update with.
     * @returns {boolean|Object} Returns false if the update fails, otherwise returns the updated object.
     */
    update(uuid, obj) {
        if (typeof (uuid) === undefined || typeof (obj) === undefined) {
            return false;
        }
        console.log('UUID OBJ ===>', obj);

        let _entry;
        let _attr;

        for (_attr in obj) {
            if (typeof (this._repository[uuid][_attr]) !== undefined) {
                this._repository[uuid][_attr] = obj[_attr];
            }
        }
        _entry = this.read(uuid);

        this.flagEntityAsDirty(uuid);
        this._repositoryClone = angular.copy(this._repository);

        return _entry;
    }

    /**
     * Removes an entry from the repository by UUID.
     * @param {string} uuid - The UUID of the entry to remove.
     */
    remove(uuid) {
        if (this._entryCache[uuid].type !== 'media') {
            this.flagEntityAsDeleted(uuid);

            if (this.isCMS) {
                this.$bridges.data().remove(uuid);
            }
        }

        delete this._repository[uuid];
        delete this._entryCache[uuid];
    }

    /**
     * Finds parent entries for a given UUID.
     * @param {Object} obj - The object to search within.
     * @param {string} uuid - The UUID to find parents for.
     * @param {Array} result - The array to store the results.
     * @param {Array} parentObj - The array to store parent objects.
     * @param {string} containerUUID - The UUID of the container.
     * @param {Object} repository - The repository object.
     * @returns {Array} The array of parent entries.
     */
    findParentEntries(obj, uuid, result, parentObj, containerUUID, repository) {
        angular.forEach(obj, (value, key) => {
            if (angular.isObject(value) || angular.isArray(value)) {
                if (value.type === 'task') {
                    const _entryObj = this.read(value.uuid);
                    if (_entryObj.isChanged()) {
                        value.sheets = _.union(value.sheets, _entryObj.originData.sheets);
                    }
                } else if (value.type === 'sheet') {
                    const _entryObj = this.read(value.uuid);
                    if (_entryObj.isChanged()) {
                        value.questionParts = _.union(value.questionParts, _entryObj.originData.questionParts);
                    }
                }

                if (!angular.isUUID(key)) {
                    if (_.where(parentObj, { uuid: obj.uuid }).length === 0) {
                        parentObj.push(obj);
                    }
                }

                if (key === 'tabs' || key === 'carousels' || key === 'iaaContainer') {
                    angular.forEach(value, (sub_container) => {
                        this.findParentEntries(sub_container, uuid, result, parentObj, containerUUID, repository);
                    });
                }
                this.findParentEntries(value, uuid, result, parentObj, containerUUID, repository);
                parentObj = [];
            } else if (value === uuid && key !== 'uuid') {
                if (angular.isUUID(containerUUID) && obj.type === 'container' && obj.uuid !== containerUUID) {
                    return false;
                } else if (parentObj.length === 0) {
                    if (_.where(result, { uuid: obj.uuid }).length === 0) {
                        if (!angular.isArray(obj)) {
                            result.push(obj);
                        }
                        if (obj.uuid && obj.type !== 'task') {
                            this.findParentEntries(repository, obj.uuid, result, [], containerUUID, repository);
                        }
                    }
                } else {
                    angular.forEach(parentObj, (parent) => {
                        if (parent.uuid) {
                            if (_.where(result, { uuid: parent.uuid }).length === 0) {
                                result.push(parent);

                                if (parent.uuid && parent.type !== 'task') {
                                    if (parent.questionPart && angular.isUUID(parent.questionPart)) {
                                        result.push(repository[parent.questionPart]);
                                        this.findParentEntries(repository, parent.questionPart, result, [], parent.uuid, repository);
                                        this.findParentEntries(repository, parent.uuid, result, [], containerUUID, repository);
                                    } else {
                                        this.findParentEntries(repository, parent.uuid, result, [], containerUUID, repository);
                                    }
                                }
                            }
                        }
                    });
                }
            } else if (value === uuid && key === 'uuid' && angular.isUUID(obj.questionPart)) {
                if (_.where(result, { uuid: obj.uuid }).length === 0) {
                    result.push(repository[obj.questionPart]);
                    this.findParentEntries(repository, obj.questionPart, result, [], obj.uuid, repository);
                }
            }
        });
        return result;
    }

    /**
     * Gets the parent entry for a given UUID.
     * @param {string} uuid - The UUID of the entry to find the parent for.
     * @param {string} [filterType] - Optional filter type to narrow down the parent entry.
     * @returns {Object|null} The parent entry object or null if not found.
     */
    getParentEntryForUUID(uuid, filterType) {
        let result = this.findParentEntries(angular.copy(this._repositoryClone), uuid, [], [], undefined, angular.copy(this._repositoryClone));
        if (filterType) {
            let resultFiltered = _.where(result, { type: filterType });
            if (filterType === 'questionPart') {
                if (resultFiltered.length === 0) {
                    resultFiltered = _.where(result, { type: 'sheet' });
                }
            }
            if (resultFiltered.length === 1) {
                result = resultFiltered[0];
            } else if (resultFiltered.length > 1) {
                result = resultFiltered;
            } else {
                result = null;
            }
        }
        return result;
    }

    /**
     * Gets the parent entries for a given UUID.
     * @param {string} uuid - The UUID of the entry to find parents for.
     * @param {boolean} existsChild - Whether the child exists (used for recursive calls).
     * @returns {Array} An array of parent entries.
     */
    getParentsEntryByUUID(uuid, existsChild) {
        if (!angular.isUUID(uuid)) {
            console.warn("In the method getParentsEntryByUUID, the parameter 'uuid' is not a UUID", uuid);
            return [];
        }

        const entry = this.read(uuid);
        let parents = [];

        switch (entry.getType()) {
            case 'task':
                return [entry]; // Tasks are top-level, so they are their own "parent"

            case 'sheet':
                // Find all tasks that contain this sheet
                const tasks = _.filter(this._repository, { 'type': 'task' });
                parents = tasks.filter(task => {
                    const taskObj = this.readEntryByUUID(task.uuid);
                    return taskObj.getSheets().some(sheet => sheet.uuid === uuid);
                }).map(task => this.read(task.uuid));
                break;

            case 'layout':
                // Find all sheets that use this layout
                const sheets = _.filter(this._repository, { 'type': 'sheet' });
                parents = sheets.filter(sheet => {
                    const sheetObj = this.read(sheet.uuid);
                    return sheetObj.getLayout() && sheetObj.getLayout().getUUID() === uuid;
                }).map(sheet => this.read(sheet.uuid));

                // Recursively get parents of these sheets
                parents = parents.concat(
                    ...parents.map(parent => this.getParentsEntryByUUID(parent.uuid, true))
                );
                break;

            case 'container':
                if (!existsChild) {
                    // Check if container is linked to a questionPart
                    if (this.exists(entry.data.questionPart) && entry.getQuestionPart()) {
                        parents.push(entry.getQuestionPart());
                    } else {
                        // Look for parent containers (tabs, carousels, iaaContainer)
                        const containers = _.filter(this._repository, { 'type': 'container' });
                        containers.forEach(container => {
                            const containerObj = this.read(container.uuid);
                            let subContainers;
                            if (containerObj.isShowTabs()) {
                                subContainers = containerObj.getTabs();
                            } else if (containerObj.isShowCarousels()) {
                                subContainers = containerObj.getCarousels();
                            } else if (containerObj.isShowIaaContainer()) {
                                subContainers = containerObj.getIaaContainer();
                            }
                            if (subContainers && subContainers.some(sc => sc.content === uuid)) {
                                parents.push(containerObj);
                            }
                        });
                    }

                    // Check if container is used in any layout
                    const layouts = _.filter(this._repository, { 'type': 'layout' });
                    layouts.forEach(layout => {
                        const layoutObj = this.read(layout.uuid);
                        const rows = layoutObj.getRows();
                        rows.forEach(row => {
                            row.cols.forEach(col => {
                                if (col.list.some(item => item.content === uuid)) {
                                    parents.push(layoutObj);
                                }
                            });
                        });
                    });

                    // Recursively get parents of found parents
                    parents = parents.concat(
                        ...parents.map(parent => this.getParentsEntryByUUID(parent.uuid, true))
                    );
                }
                break;

            case 'questionPart':
                // Find containers that use this questionPart
                const questionPartContainers = _.filter(this._repository, { 'type': 'container' })
                    .filter(container => {
                        const containerObj = this.read(container.uuid);
                        return containerObj.data &&
                            this.exists(containerObj.data.questionPart) &&
                            containerObj.getQuestionPart() &&
                            containerObj.getQuestionPart().getUUID() === uuid;
                    }).map(container => this.read(container.uuid));

                parents = parents.concat(questionPartContainers);

                // If not found in containers, check sheets
                if (parents.length === 0) {
                    const sheetsWithQuestionPart = _.filter(this._repository, { 'type': 'sheet' })
                        .filter(sheet => {
                            const sheetObj = this.read(sheet.uuid);
                            return sheetObj.getQuestionParts().some(qp => qp.uuid === uuid);
                        }).map(sheet => this.read(sheet.uuid));
                    parents = parents.concat(sheetsWithQuestionPart);
                }

                // Recursively get parents of found parents
                parents = parents.concat(
                    ...parents.map(parent => this.getParentsEntryByUUID(parent.uuid, true))
                );
                break;
            case 'contentObject':
                const questionPartContainersWithObject = _.filter(this._repository, { 'type': 'container' })
                    .filter(container => {
                        const containerObj = this.read(container.uuid);
                        return containerObj.data &&
                            Array.isArray(containerObj.getObjects()) &&
                            containerObj.getObjects().some(objectUUID => objectUUID === uuid);
                    }).map(container => this.read(container.uuid));

                parents = parents.concat(questionPartContainersWithObject);

                // Recursively get parents of found parents
                parents = parents.concat(
                    ...parents.map(parent => this.getParentsEntryByUUID(parent.uuid, false))
                );
                break;
            default:
                console.warn(`Unhandled entry type: ${entry.getType()} for UUID: ${uuid}`);
        }

        // Remove duplicates from the parents array
        return _.uniq(parents, parent => parent.uuid);
    }

    readTask(taskUUID) {
        console.warn("readTask(taskUUID) is deprecated");
        return this.readInstanceOf(taskUUID, this.Task);
    }

    readSheet(sheetUUID) {
        console.warn("readSheet(sheetUUID) is deprecated");
        return this.readInstanceOf(sheetUUID, this.Sheet);
    }

    readContainer(materialUUID) {
        console.warn("readContainer(materialUUID) is deprecated");
        return this.readInstanceOf(materialUUID, this.Container);
    }

    readQuestionPart(questionPartUUID) {
        console.warn("readQuestionPart(questionUUID) is deprecated");
        return this.readInstanceOf(questionPartUUID, this.QuestionPart);
    }

    readLayout(layoutUUID) {
        return this.readInstanceOf(layoutUUID, this.Layout);
    }

    findInstancesByType(wantedType) {
        const _instances = {};

        for (const _idx in this._repository) {
            if (wantedType === this._repository[_idx].type && !this._entriesDeleted[_idx]) {
                _instances[_idx] = this.read(_idx);
            }
        }
        return _instances;
    }

    getListOfInstanceTypes() {
        return this.getListOfUniqueValuesByKeyName('type');
    }

    getListOfUniqueValuesByKeyName(key) {
        const _objTypes = [];

        for (const _idx in this._repository) {
            if (this._repository[_idx].hasOwnProperty(key)) {
                if (_objTypes.indexOf(this._repository[_idx][key]) === -1) {
                    _objTypes.push(this._repository[_idx][key]);
                }
            }
        }
        return _objTypes;
    }

    readLayoutByTask(task) {
        let rows = [];
        let message, key, entry;

        if (task === undefined) {
            message = 'could not read layout for ';
            message += 'task';
            message += '. task was not found.';

            this.$logger.error(this, message);
        } else {
            for (key in this._repository) {
                entry = this._repository[key];

                if (typeof entry.rows === 'object') {
                    rows = entry.rows;
                    break;
                }
            }
        }

        return rows;
    }

    clearCaches() {
        this.$logger.dev(this._logIdentifier, 'clearing object caches');

        const keys = Object.keys(this._entryCache);
        let key, idx;

        for (idx in keys) {
            console.info('clearCaches  key:', key, 'value', keys[idx]);
            key = keys[idx];
            this._entryCache[idx] = undefined;
        }

        this._entryCache = {};
        this._repository = {};

        this.$objectCache.clear();
    }

    flagEntityAsDirty(entityOrUUID) {
        let uuid;

        if (angular.isString(entityOrUUID)) {
            uuid = entityOrUUID;
        } else if (angular.isObject(entityOrUUID) && angular.isFunction(entityOrUUID.getUUID)) {
            uuid = entityOrUUID.getUUID();
        } else {
            console.error("Invalid parameter passed to flagEntityAsDirty", entityOrUUID);
            return;
        }

        this._entriesDirty[uuid] = true;
    }

    flagEntityAsDeleted(entityOrUUID) {
        let uuid;

        if (angular.isString(entityOrUUID)) {
            uuid = entityOrUUID;
        } else if (angular.isObject(entityOrUUID) && angular.isFunction(entityOrUUID.getUUID)) {
            uuid = entityOrUUID.getUUID();
        } else {
            console.error("Invalid parameter passed to flagEntityAsDeleted", entityOrUUID);
            return;
        }

        this._entriesDeleted[uuid] = true;
        console.error("Flagging for deletion: ", uuid, this._entriesDeleted);
    }

    getRepository() {
        return this._repository;
    }

    getDeletedEntries() {
        return this._entriesDeleted;
    }

    fixTaskRootEntryPoint(taskUUID) {
        console.info('%cRoot Access Point - Starting self-repairing process for Task %c%s ', 'background-color:#ffd8d8', 'background-color:#ffd8d8;font-weight:700;', taskUUID);

        const _taskList = this.findInstancesByType('task');

        for (const uuid in _taskList) {
            if (taskUUID === uuid) {
                console.log("  Checking Task's root uuid: %s", uuid, _taskList[uuid].getUUID());
                if (uuid !== _taskList[uuid].getUUID()) {
                    console.log('  Updating %s with', uuid, { 'uuid': uuid });
                    this.update(uuid, { 'uuid': uuid }).save();
                }
            } else {
                console.log('  Removing old root access entry point: %s', uuid);
                this.APIService
                    .deleteEntity(
                        taskUUID,
                        uuid
                    )
                    .then(data => {
                        this.remove(uuid);
                        this.$logger.info(this._logIdentifier, 'old root access point ' + uuid + ' has been removed', data);
                    }, err => {
                        this.$logger.error(this._logIdentifier, 'delete error trying to remove old root access point ' + uuid + ' for task ' + taskUUID, err);
                    });
            }
        }
    }

    fetchContext(callback) {
        if (!this._repository) {
            this.requireTaskData((data, error) => {
                if (typeof callback === 'function') {
                    callback(this, error);
                }
            });
        } else {
            if (typeof callback === 'function') {
                callback(this, null);
            }
        }
        
        return this;
    }

    clear() {
        this._repository = undefined;
        this._entryCache = {};
        this._entriesDirty = {};
    }

    isDirty() {
        return Object.keys(this._entriesDirty).length > 0;
    }

    /**
     * Retrieves the model factory for a given model type.
     * @param {string} modelType - The type of the model.
     * @returns {Function} The factory function for the model type.
     */
    getModelFactory(modelType) {
        return this.$injector.get(modelType.charAt(0).toUpperCase() + modelType.slice(1));
    }
}

export default TaskDataService;